/**
 * The world: creation, the step function, snapshots, and the match result.
 *
 * This module is the whole public surface. The server room owns a `SimWorld`
 * and is the only authority on it; the client owns a second `SimWorld` it steps
 * with its own unacked inputs for prediction. Same code, two runtimes, and the
 * only difference is who is allowed to be right.
 *
 * Three rules hold everywhere in here:
 *
 *   1. No `Date.now()`, no `Math.random()`, no wall clock. Time is `world.tick`.
 *   2. Iteration over players is `world.players`, which is sorted by id and
 *      never reordered. Map insertion order is a divergence waiting to happen.
 *   3. Nothing a client sends is trusted for anything except which buttons are
 *      down. Position, damage, death, cooldowns, pickups and the winner are all
 *      decided here.
 */
import { getCharacter, sanitizeInputFrame, TICK_MS, Button, } from "@chaos/protocol";
import { DROP_TICKS, DT, GROUND_FLOOR_PLANE, GROUND_NONE, CAST_BUFFER_TICKS, MATCH_DURATION_TICKS, PLAYER_HALF_HEIGHT, SIMULTANEOUS_DEATH_TICKS, ZONE_HIT_EVENT_INTERVAL_TICKS, } from "./constants.js";
import { applyDamage, applyDamageOverTime, awardKill, createAux, creditFor, findPlayer, getAux, hasPendingCast, recordPath, resolvePendingCasts, startCast, stepDecoys, stepGroundZones, stepProjectiles, tryDash, } from "./combat.js";
import { resolveCapsuleVsBox, separatePlayers } from "./collision.js";
import { applyEffect, effectNames, expireEffects, hasEffect, isInvisible, isStunned } from "./effects.js";
import { onArenaFloor } from "./map.js";
import { applyFacing, applyWind, integrateMovement, land } from "./movement.js";
import { applyChaosDamage, chaosGravityMultiplier, chaosSpeedMultiplier, chaosSurface, createChaosState, serializeChaos, } from "./mutations.js";
import { digest, hashSeed, shuffled } from "./rng.js";
import { buildPlatformHash } from "./spatialHash.js";
import { ZONE_SOURCE_ID, createZone, stepZone } from "./zone.js";
/** Attribution for effects granted by map geometry rather than by a player. */
const SURFACE_SOURCE_ID = "__surface__";
/** What a player who sent nothing this tick is treated as having sent. */
export const NEUTRAL_INPUT = Object.freeze({
    tick: 0,
    seq: 0,
    buttons: 0,
    moveX: 0,
    moveZ: 0,
    yaw: 0,
});
export function createWorld(opts) {
    const map = opts.map;
    // Sorted once, here, and never touched again. Every loop in the sim depends
    // on this ordering being identical on the server and in every client.
    const roster = opts.players.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const world = {
        tick: 0,
        players: [],
        zone: {
            centerX: map.centerX,
            centerZ: map.centerZ,
            radius: map.startRadius,
            targetRadius: map.startRadius,
            dps: 0,
            startRadius: map.startRadius,
            announcedPhase: 0,
            suppressed: false,
        },
        platforms: map.platforms,
        rngState: hashSeed(opts.seed),
        matchEndTick: MATCH_DURATION_TICKS,
        ended: false,
        winnerId: null,
        endReason: null,
        map,
        seed: opts.seed,
        seedCommitment: opts.seedCommitment ?? digest(opts.seed),
        dropEndTick: DROP_TICKS,
        aux: {},
        casts: [],
        projectiles: [],
        zones: [],
        decoys: [],
        deaths: [],
        chaos: createChaosState(),
        nextEntityId: 0,
        hash: buildPlatformHash(map.platforms),
    };
    // Spawn assignment is seeded, so join order does not decide who lands next to
    // the centre. Drawn before the zone centre, and the order of these two draws
    // is itself part of the replay.
    const spawnOrder = shuffled(world, map.spawns.length > 0 ? map.spawns : [{ x: 0, y: 4, z: 0 }]);
    roster.forEach((entry, index) => {
        const spawn = spawnOrder[index % spawnOrder.length] ?? { x: 0, y: 4, z: 0 };
        const character = getCharacter(entry.characterId);
        const player = {
            id: entry.id,
            characterId: entry.characterId,
            x: spawn.x,
            y: spawn.y,
            z: spawn.z,
            vx: 0,
            vy: 0,
            vz: 0,
            yaw: 0,
            health: character.maxHealth,
            phase: "alive",
            cooldowns: {},
            ultCharge: 0,
            effects: [],
            damageDealt: 0,
            kills: 0,
            placement: null,
            grounded: false,
            joinOrder: index,
        };
        world.players.push(player);
        const aux = createAux();
        aux.prevHealth = player.health;
        world.aux[player.id] = aux;
    });
    world.zone = createZone(map, world);
    return world;
}
// ---------------------------------------------------------------------------
// The step
// ---------------------------------------------------------------------------
const scratchCandidates = [];
/**
 * Advances the world by exactly one tick and returns everything that happened.
 *
 * `inputs` is keyed by player id; a missing entry means the player sent nothing
 * this tick — a disconnect, a dropped packet, an AFK. They stand still and stay
 * killable, which is the disconnect rule from GAME-DESIGN and also the only
 * behaviour that keeps a stake in the pot honest.
 */
export function stepWorld(world, inputs) {
    const events = [];
    // A finished match is inert. Stepping it again must not move anything, or a
    // late packet after settlement could change a payout.
    if (world.ended)
        return events;
    world.tick += 1;
    if (world.tick === 1) {
        events.push({ t: "match_start", tick: world.tick, seedCommitment: world.seedCommitment });
    }
    for (const p of world.players) {
        getAux(world, p.id).prevHealth = p.health;
    }
    for (const p of world.players) {
        if (p.phase !== "alive")
            continue;
        const aux = getAux(world, p.id);
        const input = readInput(inputs, p.id);
        expireEffects(p, world.tick);
        // Aim FIRST, so a dash or a cast pressed this frame uses the facing this
        // frame reported rather than the one before it.
        applyFacing(p, input, isStunned(p));
        stepActions(world, p, input, events);
        // Chaos mutators enter the step HERE and nowhere else: they scale the two
        // movement inputs and may substitute the surface under the player's feet.
        // Nothing downstream of this call knows a mutator exists, which is what
        // keeps the mutator tier from becoming a second physics implementation.
        const surface = chaosSurface(world, surfaceUnder(world, aux.standingIndex), aux.standingIndex !== GROUND_NONE);
        applyStandingSurface(world, p, surface, events);
        const prevY = p.y;
        integrateMovement(p, aux, input, {
            characterSpeedMult: getCharacter(p.characterId).speedMult * chaosSpeedMultiplier(world),
            gravityMult: chaosGravityMultiplier(world),
            onIce: surface?.kind === "ice",
            surface,
        });
        // The fly-in cannot leave the arena. The drop is invulnerable — damage,
        // lava and killY are all suspended — so without this a player sprinting
        // outward spent the whole 5s crossing the rim and falling, and the first
        // live tick mass-killed them at killY from deep in the void. Pinning the
        // drop inside the rim means every match starts with everyone on the deck.
        if (world.tick <= world.dropEndTick)
            clampToArenaRim(world.map, p);
        resolveGeometry(world, p, prevY);
        applyWindZones(world, p);
    }
    separateBodies(world);
    resolvePendingCasts(world, events);
    stepProjectiles(world, events);
    stepGroundZones(world, events);
    stepDecoys(world);
    applyDamageOverTime(world, events);
    stepZone(world, events, (playerId, amount, report) => {
        const target = findPlayer(world, playerId);
        if (target)
            applyDamage(world, target, amount, ZONE_SOURCE_ID, "zone", events, report);
    });
    // After the zone, so environmental damage resolves in one place and in a
    // fixed order.
    applyChaosDamage(world, events);
    applyLethalGeometry(world);
    for (const p of world.players) {
        if (p.phase === "alive")
            recordPath(getAux(world, p.id), p);
    }
    stepEliminations(world, events);
    stepMatchEnd(world, events);
    return events;
}
/**
 * Re-sanitizes on the way in.
 *
 * The room sanitizes too. Doing it twice costs one allocation per player per
 * tick and means a future caller that forgets — the client predictor, a replay
 * harness, a test — still cannot feed the sim `moveX: 40` or a NaN.
 */
function readInput(inputs, id) {
    const raw = inputs.get(id);
    if (raw === undefined)
        return NEUTRAL_INPUT;
    return sanitizeInputFrame(raw) ?? NEUTRAL_INPUT;
}
/** Highest commitment first: a client mashing every bit gets its ultimate. */
const SLOT_PRIORITY = [
    { slot: "ultimate", bit: Button.Ultimate },
    { slot: "skill1", bit: Button.Skill1 },
    { slot: "skill2", bit: Button.Skill2 },
    { slot: "basic", bit: Button.Basic },
];
function pressedSlot(buttons) {
    for (const { slot, bit } of SLOT_PRIORITY) {
        if ((buttons & bit) !== 0)
            return slot;
    }
    return null;
}
function stepActions(world, p, input, events) {
    const aux = getAux(world, p.id);
    // A dash during a wind-up moved the caster out from under their own pending
    // cast — resolution reads live positions, so the attack silently whiffed
    // with the cooldown spent and no feedback. The cast is the commitment: hold
    // the dash until it resolves, so the press fires instead of vanishing.
    // Dashing during recovery stays legal — there is nothing left to whiff.
    const dashPressed = (input.buttons & Button.Dash) !== 0;
    if (dashPressed)
        aux.dashBufferTicks = CAST_BUFFER_TICKS;
    if (isStunned(p)) {
        // A tap in the last instants of a stun buffers and fires at stun-end, so
        // mashing to escape works. It decays on the normal window rather than
        // holding, because a stun is imposed, not committed to: a press from two
        // seconds ago firing on its own would surprise its own player.
        if (!dashPressed && aux.dashBufferTicks > 0)
            aux.dashBufferTicks -= 1;
        return;
    }
    if (dashPressed || aux.dashBufferTicks > 0) {
        if (hasPendingCast(world, p.id)) {
            // Held, not decremented: a wind-up can outlast the buffer, and a buffer
            // that expires while the cast is the only thing blocking it eats the
            // press with no feedback — a tap during any wind-up longer than the
            // buffer died here. The cast buffer below holds through busy the same
            // way. After the resolve the press gets its normal window.
        }
        else if (tryDash(world, p, input.moveX, input.moveZ, events)) {
            aux.dashBufferTicks = 0;
        }
        else if (!dashPressed && aux.dashBufferTicks > 0) {
            // Held on the press tick, like the cast buffer, so the window is
            // CAST_BUFFER_TICKS full ticks after the press.
            aux.dashBufferTicks -= 1;
        }
    }
    const pressed = pressedSlot(input.buttons);
    // Busy = mid wind-up or recovery. The press used to be DROPPED here, and
    // because a chain spends most of its life in one of those, pressing Q a
    // fraction early did nothing at all with no feedback — the single biggest
    // reason the kit felt unresponsive. Hold it instead, briefly, exactly like
    // the jump buffer movement has always had.
    if (hasEffect(p, "busy")) {
        if (pressed !== null) {
            aux.castBufferSlot = pressed;
            aux.castBufferTicks = CAST_BUFFER_TICKS;
        }
        return;
    }
    // Buffer bookkeeping before the cast attempts, so the window is the same
    // length whether or not anything is held this tick.
    const buffered = aux.castBufferTicks > 0 ? aux.castBufferSlot : null;
    if (aux.castBufferTicks > 0)
        aux.castBufferTicks -= 1;
    if (aux.castBufferTicks === 0)
        aux.castBufferSlot = null;
    // Every pressed slot gets a try, in priority order, and the first that
    // actually starts wins. This used to pick the SINGLE highest-priority slot
    // and cast once: the client re-sends a held button every tick, so holding R
    // before the ult was charged made that one unready slot swallow the whole
    // bitfield — the basic attack went silent for as long as the key was down.
    // Falling through keeps held-to-repeat working under an unready hold, and a
    // masher still gets its ultimate the moment it is up.
    for (const { slot, bit } of SLOT_PRIORITY) {
        if ((input.buttons & bit) === 0)
            continue;
        if (startCast(world, p, slot, events)) {
            aux.castBufferSlot = null;
            aux.castBufferTicks = 0;
            return;
        }
    }
    // A live press always beats a stale buffered one; the buffered press keeps
    // its window when everything live failed to start.
    if (buffered !== null && startCast(world, p, buffered, events)) {
        aux.castBufferSlot = null;
        aux.castBufferTicks = 0;
    }
}
/**
 * Radial projection back onto the arena disc. Projection onto a convex set is
 * non-expansive, so the per-tick displacement caps still hold on the clamped
 * path. `Math.sqrt` is IEEE-exact and replay-safe.
 */
function clampToArenaRim(map, p) {
    const dx = p.x - map.centerX;
    const dz = p.z - map.centerZ;
    const d2 = dx * dx + dz * dz;
    const r = map.startRadius;
    if (d2 <= r * r)
        return;
    const scale = r / Math.sqrt(d2);
    p.x = map.centerX + dx * scale;
    p.z = map.centerZ + dz * scale;
}
/**
 * Hazard and mend pools, charged per tick while stood on them.
 *
 * Scaled by DT for the same reason the safe zone is: a pool that ticked on a
 * timer would deal a different total depending on where in that timer you
 * stepped off, and two players standing in identical lava would take different
 * damage. Per-tick times DT is the only version a replay reproduces.
 *
 * `applyDamage` already refuses during the drop and through i-frames, so a
 * dash across a pool is a real out and landing in one on the fly-in is not a
 * death sentence. Healing checks the drop itself, since it has no such gate.
 */
function applyStandingSurface(world, p, surface, events) {
    if (!surface || p.phase !== "alive")
        return;
    if (surface.kind === "hazard") {
        // Reported on the zone's cadence rather than every tick: 30 hit events a
        // second per player is a kill feed nobody can read and a replay log nobody
        // wants to store.
        const report = world.tick % ZONE_HIT_EVENT_INTERVAL_TICKS === 0;
        applyDamage(world, p, surface.dps * DT, SURFACE_SOURCE_ID, "hazard_pool", events, report);
        return;
    }
    if (surface.kind === "heal") {
        if (world.tick <= world.dropEndTick)
            return;
        const max = getCharacter(p.characterId).maxHealth;
        // Never past full: a pad that overheals is a pad that stores health, and
        // then the pad is the whole game.
        p.health = Math.min(max, p.health + surface.hps * DT);
    }
}
function surfaceUnder(world, standingIndex) {
    if (standingIndex < 0)
        return null;
    return world.platforms[standingIndex]?.surface ?? null;
}
function resolveGeometry(world, p, prevY) {
    const contact = { grounded: false, groundIndex: GROUND_NONE, groundY: 0, ceiling: false };
    const candidates = world.hash.queryInto(p.x, p.z, scratchCandidates);
    for (const index of candidates) {
        const plat = world.platforms[index];
        if (!plat || !plat.solid)
            continue;
        resolveCapsuleVsBox(p, prevY, plat, contact, index);
    }
    // The map's floor plane, if it has one. v1's GROUND_Y clamp was infinite;
    // here it stops at the arena rim (`onArenaFloor`), because a floor that
    // extends past the rendered deck is invisible ground a player can stand on
    // in the void — past the rim you fall, and killY does the rest.
    const floor = world.map.floor;
    if (floor.kind === "solid" && onArenaFloor(world.map, p.x, p.z)) {
        const restY = floor.y + PLAYER_HALF_HEIGHT;
        if (p.y <= restY && p.vy <= 0 && (!contact.grounded || restY > contact.groundY)) {
            contact.grounded = true;
            contact.groundY = restY;
            contact.groundIndex = GROUND_FLOOR_PLANE;
        }
    }
    const aux = getAux(world, p.id);
    if (contact.grounded) {
        const plat = contact.groundIndex >= 0 ? world.platforms[contact.groundIndex] : undefined;
        land(p, aux, contact.groundY, plat?.surface ?? null);
        aux.standingIndex = contact.groundIndex;
        if (plat && plat.surface.kind === "speed") {
            // v1 held this as a `speedBoostUntil` wall-clock timestamp. As a timed
            // effect it expires on a tick count, which is the only kind of deadline
            // a replay can reproduce.
            applyEffect(p, "speed", plat.surface.durationMs, world.tick, SURFACE_SOURCE_ID, {
                magnitude: (plat.surface.multiplier - 1) * 100,
            });
        }
    }
    else {
        aux.standingIndex = GROUND_NONE;
    }
}
function applyWindZones(world, p) {
    for (const w of world.map.windZones) {
        if (Math.abs(p.x - w.x) > w.hx || Math.abs(p.y - w.y) > w.hy || Math.abs(p.z - w.z) > w.hz)
            continue;
        applyWind(p, w.fx, w.fy, w.fz);
    }
}
/**
 * Pushes overlapping bodies apart. Pairs are visited in sorted index order, so
 * the resolution is the same everywhere.
 */
function separateBodies(world) {
    for (let i = 0; i < world.players.length; i++) {
        const a = world.players[i];
        if (!a || a.phase !== "alive")
            continue;
        for (let j = i + 1; j < world.players.length; j++) {
            const b = world.players[j];
            if (!b || b.phase !== "alive")
                continue;
            separatePlayers(a, b);
        }
    }
}
/** The void, and lava. Both are lethal regardless of remaining health. */
function applyLethalGeometry(world) {
    const floor = world.map.floor;
    for (const p of world.players) {
        if (p.phase !== "alive")
            continue;
        if (world.tick <= world.dropEndTick)
            continue;
        if (p.y < world.map.killY) {
            p.health = 0;
            continue;
        }
        if (floor.kind === "lava" && p.y - PLAYER_HALF_HEIGHT <= floor.y) {
            p.health = 0;
        }
    }
}
function countAlive(world) {
    let n = 0;
    for (const p of world.players)
        if (p.phase === "alive")
            n++;
    return n;
}
function stepEliminations(world, events) {
    const dying = [];
    for (const p of world.players) {
        if (p.phase === "alive" && p.health <= 0)
            dying.push(p);
    }
    if (dying.length === 0)
        return;
    const aliveBefore = countAlive(world);
    // Simultaneous deaths are ranked by the same hierarchy that decides the
    // match, so the strongest of a mutual kill takes the better placement.
    dying.sort((a, b) => compareContenders(contenderOf(world, a, true), contenderOf(world, b, true)));
    let placement = aliveBefore - dying.length + 1;
    for (const victim of dying) {
        const aux = getAux(world, victim.id);
        const credit = creditFor(world, victim);
        victim.phase = "dead";
        victim.health = 0;
        victim.placement = placement;
        victim.effects.length = 0;
        if (credit.killerId !== null) {
            const killer = findPlayer(world, credit.killerId);
            if (killer && killer.phase === "alive")
                awardKill(world, killer, credit.abilityId);
        }
        const record = {
            playerId: victim.id,
            tick: world.tick,
            healthBefore: aux.prevHealth,
            damageDealt: victim.damageDealt,
            joinOrder: victim.joinOrder,
            killerId: credit.killerId,
            placement,
            byZone: credit.byZone,
        };
        world.deaths.push(record);
        events.push({
            t: "eliminated",
            tick: world.tick,
            victimId: victim.id,
            killerId: credit.killerId,
            placement,
        });
        placement += 1;
    }
}
function stepMatchEnd(world, events) {
    const alive = countAlive(world);
    // A single-player world is a practice range, not a match: it runs the clock
    // out rather than declaring the only participant the last one standing.
    const survivorEnd = world.players.length >= 2 && alive <= 1;
    if (alive !== 0 && !survivorEnd && world.tick < world.matchEndTick)
        return;
    const result = resolveMatchEnd(world);
    world.ended = true;
    world.winnerId = result.winnerId;
    world.endReason = result.reason;
    assignFinalPlacements(world, result.winnerId);
    events.push({ t: "match_end", tick: world.tick, winnerId: result.winnerId, reason: result.reason });
}
/**
 * Closes out placements so that they are exactly a permutation of 1..n.
 *
 * Two cases. Survivors (one on a normal finish, several on a clock expiry) are
 * ranked by the same hierarchy that picked the winner and take the top slots.
 * A total wipe is subtler: the 250ms simultaneity window can hand the win to
 * someone who went down on an earlier tick and was already assigned a
 * placement, so the winner swaps slots with whoever currently holds first —
 * otherwise two players finish 1st and the payout table has two rows.
 */
function assignFinalPlacements(world, winnerId) {
    const alive = world.players.filter((p) => p.phase === "alive");
    if (alive.length > 0) {
        const ranked = alive.map((p) => contenderOf(world, p, false)).sort(compareContenders);
        ranked.forEach((c, i) => {
            const p = findPlayer(world, c.id);
            if (p)
                p.placement = i + 1;
        });
        return;
    }
    if (winnerId === null)
        return;
    const winner = findPlayer(world, winnerId);
    if (!winner || winner.placement === 1)
        return;
    const holder = world.players.find((p) => p.placement === 1);
    const previous = winner.placement;
    winner.placement = 1;
    if (holder && previous !== null)
        holder.placement = previous;
}
/**
 * A living player is ranked on the HP they have right now. A player being
 * eliminated is ranked on the HP they had at the START of the tick that killed
 * them — everyone in a mutual kill is on zero by the time it is scored, so
 * current HP would tie every simultaneous death and push the decision down to
 * damage dealt.
 */
function contenderOf(world, p, dying) {
    return {
        id: p.id,
        health: dying ? getAux(world, p.id).prevHealth : p.health,
        damageDealt: p.damageDealt,
        joinOrder: p.joinOrder,
    };
}
/** Higher HP, then more damage, then earlier join. Total, and never random. */
function compareContenders(a, b) {
    if (a.health !== b.health)
        return b.health - a.health;
    if (a.damageDealt !== b.damageDealt)
        return b.damageDealt - a.damageDealt;
    if (a.joinOrder !== b.joinOrder)
        return a.joinOrder - b.joinOrder;
    // joinOrder is unique per lobby, so this is unreachable in practice. It is
    // here because a comparator that can return 0 for two distinct players makes
    // the sort implementation-defined, and a payout must not be.
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
/**
 * Decides the match as if it ended on this tick.
 *
 * The hierarchy, from GAME-DESIGN:
 *
 *   last standing
 *   simultaneous death  -> higher HP on the prior tick -> most damage -> earliest join
 *   clock expiry, >1 alive -> highest HP -> most damage -> earliest join
 *
 * There is no branch that returns a refund, because a refund reachable through
 * play makes stalling a strategy: the moment "survive the clock" returns your
 * buy-in, the correct line for over half the lobby is to stop playing. The only
 * null winner is a match nobody ever entered, which is a server fault and is
 * settled off the input log, not by the sim.
 */
export function resolveMatchEnd(world) {
    const alive = [];
    for (const p of world.players)
        if (p.phase === "alive")
            alive.push(p);
    if (alive.length === 1) {
        const only = alive[0];
        if (only)
            return { winnerId: only.id, reason: "last_standing" };
    }
    if (alive.length > 1) {
        const best = alive.map((p) => contenderOf(world, p, false)).sort(compareContenders)[0];
        return { winnerId: best ? best.id : null, reason: "timeout" };
    }
    // Nobody is alive. The winner is the best of whoever went down last.
    if (world.deaths.length === 0) {
        return { winnerId: null, reason: "aborted_refund" };
    }
    let lastTick = -1;
    for (const d of world.deaths)
        if (d.tick > lastTick)
            lastTick = d.tick;
    const contenders = [];
    for (const d of world.deaths) {
        if (d.tick < lastTick - SIMULTANEOUS_DEATH_TICKS)
            continue;
        contenders.push({
            id: d.playerId,
            health: d.healthBefore,
            damageDealt: d.damageDealt,
            joinOrder: d.joinOrder,
        });
    }
    const best = contenders.sort(compareContenders)[0];
    return { winnerId: best ? best.id : null, reason: "zone_wipe_highest_damage" };
}
// ---------------------------------------------------------------------------
// Snapshots
// ---------------------------------------------------------------------------
/**
 * Builds the view of the world that one player is allowed to see.
 *
 * The filtering is the point. v1 broadcast full opponent state to every client
 * 20 times a second with no filtering at all (77 plain-JSON `broadcast()` calls,
 * zero Schema), which is a free wallhack and a free radar for anyone willing to
 * open devtools. Here an invisible opponent is not dimmed client-side — they
 * are absent from the bytes.
 */
export function toSnapshot(world, forPlayerId, lastProcessedSeq) {
    const players = [];
    for (const p of world.players) {
        const invisible = p.phase === "alive" && isInvisible(p);
        if (invisible && p.id !== forPlayerId)
            continue;
        players.push({
            id: p.id,
            characterId: p.characterId,
            x: p.x,
            y: p.y,
            z: p.z,
            vx: p.vx,
            vy: p.vy,
            vz: p.vz,
            yaw: p.yaw,
            health: p.health,
            phase: p.phase,
            cooldowns: { ...p.cooldowns },
            ultCharge: p.ultCharge,
            effects: effectNames(p),
            visible: !invisible,
        });
    }
    // Decoys ride the same shape as players on purpose: a decoy that could be
    // told apart by its wire representation is not a decoy.
    for (const d of world.decoys) {
        const owner = findPlayer(world, d.ownerId);
        players.push({
            id: d.id,
            characterId: d.characterId,
            x: d.x,
            y: d.y,
            z: d.z,
            vx: 0,
            vy: 0,
            vz: 0,
            yaw: d.yaw,
            health: owner ? owner.health : 1,
            phase: "alive",
            cooldowns: {},
            ultCharge: 0,
            effects: [],
            visible: true,
        });
    }
    return {
        tick: world.tick,
        lastProcessedSeq,
        players,
        zone: {
            centerX: world.zone.centerX,
            centerZ: world.zone.centerZ,
            radius: world.zone.radius,
            targetRadius: world.zone.targetRadius,
            dps: world.zone.dps,
        },
        aliveCount: countAlive(world),
        timeRemainingMs: Math.max(0, (world.matchEndTick - world.tick) * TICK_MS),
    };
}
// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------
/**
 * Canonical serialization of everything the simulation decides.
 *
 * Written out by hand rather than `JSON.stringify(world)` for two reasons: key
 * order has to be fixed, and `map`/`hash` are excluded because they are inputs
 * and a derived index, not state. Paired with the input log this is what makes
 * a disputed match settleable with evidence rather than an apology — replay the
 * inputs, compare the digests, and the argument is over.
 */
export function serializeWorld(world) {
    const parts = [];
    parts.push(`t=${world.tick}`);
    parts.push(`rng=${world.rngState}`);
    parts.push(`end=${world.ended ? 1 : 0}:${world.winnerId ?? ""}:${world.endReason ?? ""}`);
    parts.push(`zone=${num(world.zone.centerX)},${num(world.zone.centerZ)},${num(world.zone.radius)},${num(world.zone.dps)},${world.zone.announcedPhase}`);
    parts.push(`ents=${world.nextEntityId}`);
    // The ruleset the match was played under, in the digest the verifier compares.
    for (const line of serializeChaos(world.chaos))
        parts.push(line);
    for (const p of world.players) {
        const aux = getAux(world, p.id);
        const cds = Object.keys(p.cooldowns)
            .sort()
            .map((k) => `${k}:${p.cooldowns[k] ?? 0}`)
            .join("|");
        const fx = p.effects
            .map((e) => `${e.kind}:${e.untilTick}:${num(e.magnitude)}:${e.sourceId}:${num(e.ax)}:${num(e.ay)}:${num(e.az)}`)
            .join("|");
        parts.push(`p[${p.id}]=${num(p.x)},${num(p.y)},${num(p.z)},${num(p.vx)},${num(p.vy)},${num(p.vz)},${num(p.yaw)},` +
            `${num(p.health)},${p.phase},${num(p.ultCharge)},${num(p.damageDealt)},${p.kills},${p.placement ?? -1},` +
            `${p.grounded ? 1 : 0},${p.joinOrder};cd=${cds};fx=${fx};` +
            `aux=${aux.coyoteTicks},${aux.jumpBufferTicks},${aux.jumpHeld ? 1 : 0},${aux.isJumping ? 1 : 0},` +
            `${num(aux.prevHealth)},${aux.lastHitById ?? ""},${aux.lastHitAbilityId},${aux.lastHitTick},${aux.lastZoneTick},` +
            `${aux.pathHead},${aux.standingIndex},${aux.wasDashing ? 1 : 0},${aux.dashBufferTicks}`);
    }
    for (const c of world.casts)
        parts.push(`c=${c.playerId},${c.abilityId},${c.slot},${c.resolveTick},${num(c.yaw)}`);
    for (const pr of world.projectiles) {
        parts.push(`pr=${pr.id},${pr.ownerId},${num(pr.x)},${num(pr.y)},${num(pr.z)},${num(pr.vx)},${num(pr.vy)},${num(pr.vz)},${num(pr.remainingRange)}`);
    }
    for (const z of world.zones) {
        parts.push(`z=${z.id},${z.ownerId},${z.kind},${num(z.x)},${num(z.z)},${z.expiresTick},${z.pulsesLeft},${z.nextPulseTick}`);
    }
    for (const d of world.decoys)
        parts.push(`d=${d.id},${d.ownerId},${num(d.x)},${num(d.y)},${num(d.z)},${d.cursor}`);
    for (const d of world.deaths) {
        parts.push(`x=${d.playerId},${d.tick},${num(d.healthBefore)},${num(d.damageDealt)},${d.joinOrder},${d.killerId ?? ""},${d.placement},${d.byZone ? 1 : 0}`);
    }
    return parts.join("\n");
}
/** A short digest of the world, cheap enough to log every tick into the input log. */
export function worldChecksum(world) {
    return digest(serializeWorld(world));
}
/** Exact round-trip for a double, so a checksum never hides a 1-ULP divergence. */
function num(v) {
    return Number.isFinite(v) ? v.toExponential(17) : String(v);
}
/** Convenience for callers that want health as an integer for display. */
export function displayHealth(p) {
    return Math.max(0, Math.ceil(p.health));
}
//# sourceMappingURL=world.js.map